import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  PackMetadataInput,
  type PackMetadataInputT,
  type PackKindT,
} from '@skey/manifest';

/**
 * Shared metadata form for `/packs/new` and `/packs/:id`.
 *
 * The schema lives in `@skey/manifest` so the Worker and the dashboard
 * agree on the exact validation rules (Requirement 22.1, 26.3).
 *
 * Field semantics:
 *   - `name`        Required. 1-80 chars after trim.
 *   - `description` Optional. 0-500 chars.
 *   - `kind`        Required. `sticker` or `gif`. The editor flow
 *                   disables this field once the pack exists because
 *                   the Worker rejects PATCH attempts to change `kind`
 *                   (see `disallowedPatchFields` in
 *                   `routes/admin/packs.ts` — flipping kind would
 *                   invalidate every uploaded asset's content type).
 *   - `coverImage`  Optional URL. Currently NOT sent to the Worker
 *                   because `POST/PATCH /admin/v1/packs` does not
 *                   consume an external URL — the Worker tracks
 *                   `cover_asset_id` only. Kept in the form for
 *                   forward compat (Requirement 22.1 lists it as an
 *                   editable field).
 *   - `tags`        Optional. 0-10 short strings. Same forward-compat
 *                   note: not yet sent to the Worker.
 *
 * The form is purposefully un-styled at the page edges: the parent
 * lays out the surrounding chrome (sticky header, save button row).
 *
 * Validates Requirements 22.1, 25.1, 26.3.
 */

export interface PackMetadataFormProps {
  /** Initial values for an existing pack; omit to render an empty form. */
  initialValues?: Partial<PackMetadataInputT>;
  /** Disable the kind picker (true on the editor route). */
  lockKind?: boolean;
  /** Disable every input (e.g. while a save is in flight). */
  disabled?: boolean;
  /** Submit label override; defaults to "Lưu". */
  submitLabel?: string;
  /** Optional cancel button rendered next to submit. */
  onCancel?: () => void;
  /**
   * Submit handler. Receives validated, defaults-applied values. The
   * caller decides whether to POST a new pack or PATCH an existing
   * one — the form does not know.
   */
  onSubmit: (values: PackMetadataInputT) => Promise<void> | void;
}

/**
 * Default values applied when `initialValues` is omitted (i.e. on the
 * `/packs/new` route). Keeping them in a constant makes the form's
 * `defaultValues` deterministic between renders.
 */
const EMPTY_DEFAULTS: PackMetadataInputT = {
  name: '',
  description: '',
  kind: 'sticker',
  priceVnd: 0,
  tags: [],
};
export default function PackMetadataForm({
  initialValues,
  lockKind = false,
  disabled = false,
  submitLabel = 'Lưu',
  onCancel,
  onSubmit,
}: PackMetadataFormProps): JSX.Element {
  // Merge initial values into the empty defaults so a partially-filled
  // pack (e.g. no description yet) hydrates without leaving a field
  // `undefined` (RHF + Zod require concrete defaults to seed the form).
  const defaults = useMemo<PackMetadataInputT>(() => {
    return { ...EMPTY_DEFAULTS, ...(initialValues ?? {}) };
  }, [initialValues]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<PackMetadataInputT>({
    resolver: zodResolver(PackMetadataInput),
    defaultValues: defaults,
  });

  // When the pack changes (e.g. parent refetched after a save), drop the
  // dirty flag and re-seed with the new server values. RHF's `reset`
  // is idempotent on the same input.
  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  // Tags use a controller so we can render them as chips. The raw
  // `tags` array lives in form state; the input below is just a
  // typing surface. New chips commit on Enter or comma.
  const [tagDraft, setTagDraft] = useState('');

  const submit = handleSubmit(async (values) => {
    // Trim + drop empty optionals so the JSON sent to the Worker is
    // tidy. RHF gives us the raw values; the Zod resolver has already
    // applied `.trim()` for the `name` and the `coverImage` URL but
    // not the description (we trim manually here for parity).
    const normalised: PackMetadataInputT = {
      name: values.name.trim(),
      description:
        values.description !== undefined ? values.description.trim() : '',
      kind: values.kind,
      priceVnd: values.priceVnd ?? 0,
      tags: values.tags ?? [],
    };
    await onSubmit(normalised);
  });

  return (
    <form
      className="space-y-5"
      onSubmit={submit}
      noValidate
      aria-busy={isSubmitting}
    >
      <div>
        <label
          htmlFor="pack-name"
          className="block text-sm font-medium text-slate-700"
        >
          Tên pack <span className="text-red-500">*</span>
        </label>
        <input
          id="pack-name"
          type="text"
          autoComplete="off"
          maxLength={80}
          disabled={disabled || isSubmitting}
          aria-invalid={errors.name ? 'true' : 'false'}
          {...register('name')}
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
        />
        {errors.name ? (
          <p className="mt-1 text-xs text-red-600" role="alert">
            {errors.name.message}
          </p>
        ) : (
          <p className="mt-1 text-xs text-slate-500">Tối đa 80 ký tự.</p>
        )}
      </div>

      <div>
        <label
          htmlFor="pack-description"
          className="block text-sm font-medium text-slate-700"
        >
          Mô tả
        </label>
        <textarea
          id="pack-description"
          rows={3}
          maxLength={500}
          disabled={disabled || isSubmitting}
          aria-invalid={errors.description ? 'true' : 'false'}
          {...register('description')}
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
        />
        {errors.description ? (
          <p className="mt-1 text-xs text-red-600" role="alert">
            {errors.description.message}
          </p>
        ) : (
          <p className="mt-1 text-xs text-slate-500">Tối đa 500 ký tự.</p>
        )}
      </div>

      <div>
        <span className="block text-sm font-medium text-slate-700">
          Loại pack <span className="text-red-500">*</span>
        </span>
        <Controller
          control={control}
          name="kind"
          render={({ field }) => (
            <div className="mt-1 flex gap-3" role="radiogroup">
              <KindOption
                value="sticker"
                label="Sticker"
                description="PNG hoặc WebP, tĩnh"
                checked={field.value === 'sticker'}
                disabled={disabled || lockKind || isSubmitting}
                onSelect={() => field.onChange('sticker' satisfies PackKindT)}
              />
              <KindOption
                value="gif"
                label="GIF"
                description="GIF động"
                checked={field.value === 'gif'}
                disabled={disabled || lockKind || isSubmitting}
                onSelect={() => field.onChange('gif' satisfies PackKindT)}
              />
            </div>
          )}
        />
        {lockKind ? (
          <p className="mt-1 text-xs text-slate-500">
            Không thể đổi loại của pack đã tồn tại.
          </p>
        ) : null}
      </div>

      <div>
        <label
          htmlFor="pack-price"
          className="block text-sm font-medium text-slate-700"
        >
          Giá (VND)
        </label>
        <input
          id="pack-price"
          type="number"
          min={0}
          step={1000}
          inputMode="numeric"
          disabled={disabled || isSubmitting}
          aria-invalid={errors.priceVnd ? 'true' : 'false'}
          {...register('priceVnd', { valueAsNumber: true })}
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
        />
        {errors.priceVnd ? (
          <p className="mt-1 text-xs text-red-600" role="alert">
            {errors.priceVnd.message}
          </p>
        ) : (
          <p className="mt-1 text-xs text-slate-500">0 nghĩa là miễn phí.</p>
        )}
      </div>

      <div>
        <span className="block text-sm font-medium text-slate-700">
          Thẻ
        </span>
        <Controller
          control={control}
          name="tags"
          render={({ field }) => (
            <div className="mt-1 space-y-2">
              <div
                className="flex flex-wrap gap-2 rounded-md border border-slate-300 bg-white p-2"
                aria-label="Danh sách thẻ"
              >
                {(field.value ?? []).map((tag, idx) => (
                  <span
                    key={`${tag}-${idx}`}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                  >
                    {tag}
                    <button
                      type="button"
                      aria-label={`Xoá thẻ ${tag}`}
                      onClick={() => {
                        const next = (field.value ?? []).filter(
                          (_, i) => i !== idx,
                        );
                        field.onChange(next);
                      }}
                      disabled={disabled || isSubmitting}
                      className="text-slate-500 hover:text-red-600 disabled:cursor-not-allowed"
                    >
                      ✕
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const value = tagDraft.trim();
                      if (value.length === 0) return;
                      const current = field.value ?? [];
                      if (current.length >= 10) return;
                      if (current.includes(value)) {
                        setTagDraft('');
                        return;
                      }
                      field.onChange([...current, value]);
                      setTagDraft('');
                    } else if (
                      e.key === 'Backspace' &&
                      tagDraft.length === 0 &&
                      (field.value ?? []).length > 0
                    ) {
                      const current = field.value ?? [];
                      field.onChange(current.slice(0, -1));
                    }
                  }}
                  disabled={
                    disabled ||
                    isSubmitting ||
                    (field.value ?? []).length >= 10
                  }
                  placeholder="Nhập thẻ rồi nhấn Enter"
                  className="min-w-[10ch] flex-1 bg-transparent px-1 py-0.5 text-sm focus:outline-none disabled:text-slate-400"
                />
              </div>
              {errors.tags ? (
                <p className="text-xs text-red-600" role="alert">
                  {errors.tags.message ?? 'Tối đa 10 thẻ'}
                </p>
              ) : (
                <p className="text-xs text-slate-500">
                  Tối đa 10 thẻ, mỗi thẻ dưới 32 ký tự. Máy chủ hiện chưa
                  tiêu thụ trường này.
                </p>
              )}
            </div>
          )}
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={disabled || isSubmitting}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Huỷ
          </button>
        ) : null}
        <button
          type="submit"
          disabled={disabled || isSubmitting || !isDirty}
          className="inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {isSubmitting ? 'Đang lưu…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

interface KindOptionProps {
  value: PackKindT;
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function KindOption({
  value,
  label,
  description,
  checked,
  disabled,
  onSelect,
}: KindOptionProps): JSX.Element {
  return (
    <label
      className={`flex flex-1 cursor-pointer items-start gap-3 rounded-md border p-3 text-sm shadow-sm transition-colors ${
        checked
          ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
          : 'border-slate-300 bg-white hover:border-slate-400'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <input
        type="radio"
        name="pack-kind"
        value={value}
        checked={checked}
        onChange={onSelect}
        disabled={disabled}
        className="mt-0.5"
      />
      <span className="flex-1">
        <span className="block font-medium text-slate-900">{label}</span>
        <span className="block text-xs text-slate-500">{description}</span>
      </span>
    </label>
  );
}
